import { useEffect, useMemo, useRef, useState } from "react";
import { FileText, Sparkles, UploadCloud, X } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { useExpenses } from "../../lib/expenses/useExpenses";
import { useCategories } from "../../lib/categories/useCategories";
import { useTaxPolicies } from "../../lib/taxPolicies/useTaxPolicies";
import { trackEvent } from "../../lib/telemetry/analytics";
import { extractReceipt, type OcrResult } from "../../lib/ocr/client";
import { matchVendor } from "../../lib/ocr/vendorMatch";
import { vendorDefaults } from "../../lib/ocr/vendorDefaults";
import type { NewExpenseInput } from "../../lib/expenses/types";
import { useOcrPreference } from "../../lib/ocr/useOcrPreference";

interface NewExpenseDialogProps {
  open: boolean;
  onOpenChange: (next: boolean) => void;
  initialValues?: Partial<NewExpenseInput>;
  mode?: "create" | "edit";
  expenseId?: string;
}

const ACCEPT = "image/*,application/pdf";

// Parse a user-entered dollar amount ("129.00", "$1,234.56", "5") into cents.
// Returns null if invalid.
function parseAmountToCents(input: string): number | null {
  const trimmed = input.trim().replace(/[$,\s]/g, "");
  if (trimmed === "") return null;
  if (!/^\d+(\.\d{0,2})?$/.test(trimmed)) return null;
  const asFloat = Number.parseFloat(trimmed);
  if (!Number.isFinite(asFloat)) return null;
  return Math.round(asFloat * 100);
}

function centsToInput(cents: number | undefined): string {
  if (cents === undefined || cents === null) return "";
  return (cents / 100).toFixed(2);
}

export function NewExpenseDialog({
  open,
  onOpenChange,
  initialValues,
  mode = "create",
  expenseId,
}: NewExpenseDialogProps) {
  const { add, update } = useExpenses();
  const { categories } = useCategories();
  const { taxPolicies } = useTaxPolicies();

  const [vendor, setVendor] = useState("");
  const [occurredOn, setOccurredOn] = useState("");
  const [amountText, setAmountText] = useState("");
  const [categoryId, setCategoryId] = useState<string>("");
  const [taxPolicyId, setTaxPolicyId] = useState<string>("");
  const [businessPurpose, setBusinessPurpose] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  // When a category is selected and has matching policies, hide the
  // non-matching ones behind a "Show all policies" expander. Starts collapsed
  // each time the category changes (see useEffect below).
  const [showAllPolicies, setShowAllPolicies] = useState(false);
  // OCR auto-fill state. `ocrChips` tracks which fields were most recently
  // populated by OCR so we can render a "Suggested by OCR" chip next to each
  // and clear it when the user touches the field.
  const [ocrChips, setOcrChips] = useState<Record<string, boolean>>({});
  const [ocrLoading, setOcrLoading] = useState(false);
  const [ocrError, setOcrError] = useState<string | null>(null);
  // Wave 14 — chips for category/tax-policy prefills driven from the
  // matched vendor's history. Separate from the OCR chips so they don't
  // collide; dismissable independently.
  const [vendorDefaultChips, setVendorDefaultChips] = useState<{
    vendorLabel: string;
    category: boolean;
    taxPolicy: boolean;
  } | null>(null);
  // Per-field inline validation errors shown under each required input.
  // Cleared on the next change to that field.
  const [errors, setErrors] = useState<Record<string, string>>({});
  const inputRef = useRef<HTMLInputElement>(null);
  const { autoFill, setAutoFill } = useOcrPreference();

  // Split tax policies into "suggested" (category match) vs. "other". When no
  // category is selected, there are no suggestions and we fall through to
  // showing everything.
  const { suggestedPolicies, otherPolicies, selectedCategoryLabel } = useMemo(() => {
    const selected = categories.find((c) => c.id === categoryId);
    if (!selected) {
      return {
        suggestedPolicies: [],
        otherPolicies: taxPolicies,
        selectedCategoryLabel: null as string | null,
      };
    }
    const suggested = taxPolicies.filter((p) => p.categoryId === selected.id);
    const other = taxPolicies.filter((p) => p.categoryId !== selected.id);
    return {
      suggestedPolicies: suggested,
      otherPolicies: other,
      selectedCategoryLabel: selected.label,
    };
  }, [categories, taxPolicies, categoryId]);

  const hasSuggestions = suggestedPolicies.length > 0;

  // Reset form state each time the dialog opens. Seed from initialValues
  // when provided (edit mode).
  useEffect(() => {
    if (!open) {
      // Reset after close so the next open starts clean.
      setError(null);
      setSubmitting(false);
      setDragActive(false);
      setOcrChips({});
      setOcrLoading(false);
      setOcrError(null);
      setErrors({});
      setVendorDefaultChips(null);
      return;
    }
    setVendor(initialValues?.vendor ?? "");
    setOccurredOn(initialValues?.occurredOn ?? "");
    setAmountText(
      initialValues?.amountCents !== undefined
        ? centsToInput(initialValues.amountCents)
        : "",
    );
    setCategoryId(initialValues?.categoryId ?? "");
    setTaxPolicyId(initialValues?.taxPolicyId ?? "");
    setBusinessPurpose(initialValues?.businessPurpose ?? "");
    setFile(null);
    setPreview(null);
    setError(null);
    setSubmitting(false);
    setDragActive(false);
    setShowAllPolicies(false);
    setOcrChips({});
    setOcrLoading(false);
    setOcrError(null);
    setErrors({});
    setVendorDefaultChips(null);
  }, [open, initialValues]);

  // Wave 14 — when the vendor field contains a name that fuzzy-matches an
  // existing history vendor with high confidence, pre-fill category + tax
  // policy from the mode of that vendor's past expenses. Only fills
  // fields that are currently empty (never overwrites a user's pick) and
  // only renders chips for the fields we actually filled. Ignored in
  // edit mode — the existing values there are authoritative.
  useEffect(() => {
    if (!open || mode !== "create") return;
    const trimmed = vendor.trim();
    // Debounce the match so every keystroke doesn't churn the store.
    const handle = window.setTimeout(() => {
      if (trimmed.length < 3) {
        setVendorDefaultChips(null);
        return;
      }
      const match = matchVendor([trimmed]);
      if (
        !match ||
        match.source !== "history" ||
        !match.vendorId ||
        match.score <= 0.85
      ) {
        setVendorDefaultChips(null);
        return;
      }
      const defaults = vendorDefaults(match.vendorId);
      if (!defaults) {
        setVendorDefaultChips(null);
        return;
      }
      let filledCategory = false;
      let filledTaxPolicy = false;
      if (!categoryId && defaults.categoryId) {
        setCategoryId(defaults.categoryId);
        filledCategory = true;
      }
      if (!taxPolicyId && defaults.taxPolicyId) {
        setTaxPolicyId(defaults.taxPolicyId);
        filledTaxPolicy = true;
      }
      if (filledCategory || filledTaxPolicy) {
        setVendorDefaultChips({
          vendorLabel: match.canonicalName,
          category: filledCategory,
          taxPolicy: filledTaxPolicy,
        });
      } else {
        setVendorDefaultChips(null);
      }
    }, 250);
    return () => window.clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vendor, open, mode]);

  // Collapse "Show all policies" whenever the user picks a different
  // category. Keeping it collapsed is the safe default — if the new category
  // has its own suggestions, those should be front and center.
  useEffect(() => {
    setShowAllPolicies(false);
  }, [categoryId]);

  useEffect(() => {
    if (!file) {
      setPreview(null);
      return;
    }
    const isPreviewable =
      file.type.startsWith("image/") || file.type === "application/pdf";
    if (!isPreviewable) {
      setPreview(null);
      return;
    }
    const url = URL.createObjectURL(file);
    setPreview(url);
    return () => {
      URL.revokeObjectURL(url);
    };
  }, [file]);

  function handleFiles(files: FileList | null) {
    setError(null);
    if (!files || files.length === 0) return;
    const picked = files[0];
    if (!picked) return;
    const isImage = picked.type.startsWith("image/");
    const isPdf = picked.type === "application/pdf";
    if (!isImage && !isPdf) {
      setError("Unsupported file type. Please upload an image or PDF.");
      return;
    }
    setFile(picked);
    if (autoFill) {
      handleOcrForFile(picked);
    }
  }

  function applyOcrResult(result: OcrResult) {
    setVendor(result.vendor);
    setOccurredOn(result.occurredOn);
    setAmountText((result.amountCents / 100).toFixed(2));
    setOcrChips({ vendor: true, occurredOn: true, amount: true });
  }

  async function handleOcrForFile(f: File) {
    setOcrLoading(true);
    setOcrError(null);
    try {
      const result = await extractReceipt(f);
      applyOcrResult(result);
    } catch (err) {
      setOcrError(err instanceof Error ? err.message : "OCR failed");
    } finally {
      setOcrLoading(false);
    }
  }

  async function handleOcr() {
    if (!file) return;
    await handleOcrForFile(file);
  }

  function validate(): Record<string, string> {
    const e: Record<string, string> = {};
    if (!vendor.trim()) e.vendor = "Vendor is required";
    if (!occurredOn) e.occurredOn = "Date is required";
    const cents = parseAmountToCents(amountText);
    if (!amountText || cents === null || cents <= 0)
      e.amount = "Amount is required";
    if (!categoryId) e.categoryId = "Category is required";
    return e;
  }

  async function handleSubmit() {
    setError(null);

    const errs = validate();
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;

    // All required fields validated above; safe to parse amount.
    const cents = parseAmountToCents(amountText)!;

    const selectedCategory = categories.find((c) => c.id === categoryId);
    const selectedTaxPolicy = taxPolicyId
      ? taxPolicies.find((p) => p.id === taxPolicyId)
      : undefined;

    setSubmitting(true);
    try {
      if (mode === "edit" && expenseId) {
        await update(expenseId, {
          vendor: vendor.trim(),
          occurredOn,
          amountCents: cents,
          categoryId,
          categoryLabel: selectedCategory?.label ?? null,
          taxPolicyId: taxPolicyId || null,
          taxPolicyLabel: selectedTaxPolicy?.category ?? null,
          businessPurpose: businessPurpose.trim() || null,
        });
      } else {
        await add({
          vendor: vendor.trim(),
          occurredOn,
          amountCents: cents,
          categoryId,
          categoryLabel: selectedCategory?.label ?? null,
          taxPolicyId: taxPolicyId || null,
          taxPolicyLabel: selectedTaxPolicy?.category ?? null,
          businessPurpose: businessPurpose.trim() || undefined,
          file: file ?? undefined,
        });
        trackEvent("expense_created", {
          has_receipt: Boolean(file),
          has_tax_policy: Boolean(taxPolicyId),
        });
      }
      onOpenChange(false);
    } catch (err) {
      const message = err instanceof Error ? err.message : "Submit failed";
      setError(message);
    } finally {
      setSubmitting(false);
    }
  }

  const isImage = file?.type.startsWith("image/") ?? false;
  const isPdf = file?.type === "application/pdf";

  const title = mode === "edit" ? "Edit expense" : "New expense";
  const description =
    mode === "edit"
      ? "Update the fields below. Attach receipts from the Inbox detail pane."
      : "Enter the details of the expense. A receipt is optional — if omitted, the expense will be marked as needing info.";
  const submitLabel = mode === "edit" ? "Save changes" : "Create expense";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="new-expense-dialog"
        className="sm:max-w-lg max-h-[90vh] overflow-y-auto"
      >
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          {mode === "create" && (
            <div
              data-testid="new-expense-receipt-section"
              className="flex flex-col gap-2"
            >
              <div className="flex items-center justify-between">
                <label className="flex items-center gap-2 text-xs text-slate-500 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    data-testid="ocr-autofill-toggle"
                    checked={autoFill}
                    onChange={(e) => setAutoFill(e.target.checked)}
                    className="rounded border-slate-300 text-blue-600 focus:ring-blue-500 w-3.5 h-3.5"
                  />
                  Auto-fill from receipt
                </label>
              </div>
              {!file ? (
                <label
                  data-testid="new-expense-dropzone"
                  onDragEnter={(e) => {
                    e.preventDefault();
                    setDragActive(true);
                  }}
                  onDragOver={(e) => {
                    e.preventDefault();
                    setDragActive(true);
                  }}
                  onDragLeave={(e) => {
                    e.preventDefault();
                    setDragActive(false);
                  }}
                  onDrop={(e) => {
                    e.preventDefault();
                    setDragActive(false);
                    handleFiles(e.dataTransfer.files);
                  }}
                  className={`flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed p-8 text-center cursor-pointer transition-colors ${
                    dragActive
                      ? "border-blue-400 bg-blue-50"
                      : "border-slate-200 bg-slate-50 hover:border-slate-300"
                  }`}
                >
                  <UploadCloud className="w-8 h-8 text-slate-400" />
                  <div className="text-sm text-slate-700 font-medium">
                    Drag & drop a receipt, or click to browse
                  </div>
                  <div className="text-xs text-slate-500">
                    Image (PNG, JPG) or PDF — we'll fill the fields for you
                  </div>
                  <input
                    ref={inputRef}
                    data-testid="new-expense-file"
                    type="file"
                    accept={ACCEPT}
                    className="sr-only"
                    onChange={(e) => handleFiles(e.target.files)}
                  />
                </label>
              ) : (
                <>
                  <div className="relative w-full rounded-xl border border-slate-200 bg-slate-50 overflow-hidden">
                    <div className="aspect-[4/3] flex items-center justify-center">
                      {isImage && preview ? (
                        <img
                          data-testid="new-expense-image-preview"
                          src={preview}
                          alt="Selected receipt"
                          className="object-contain w-full h-full"
                        />
                      ) : isPdf && preview ? (
                        <object
                          data-testid="new-expense-pdf-preview"
                          data={`${preview}#toolbar=0&navpanes=0`}
                          type="application/pdf"
                          className="w-full h-full"
                          aria-label={file.name}
                        >
                          <div className="flex flex-col items-center gap-2 text-slate-500">
                            <FileText className="w-8 h-8" />
                            <div className="text-xs">PDF preview unavailable</div>
                          </div>
                        </object>
                      ) : (
                        <div className="flex flex-col items-center gap-2 text-slate-400">
                          <FileText className="w-10 h-10" />
                          <div className="text-xs font-medium">
                            {file.name}
                          </div>
                        </div>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => setFile(null)}
                      className="absolute top-2 right-2 p-1.5 bg-white/90 backdrop-blur text-slate-600 hover:text-slate-900 hover:bg-white rounded-md shadow-sm border border-slate-200"
                      aria-label="Clear selected file"
                    >
                      <X className="w-4 h-4" />
                    </button>
                    <div className="absolute bottom-0 inset-x-0 px-3 py-2 bg-gradient-to-t from-black/50 to-transparent text-white text-xs font-medium truncate">
                      <span data-testid="new-expense-filename">
                        {file.name}
                      </span>
                      <span className="opacity-80">
                        {" · "}
                        {(file.size / 1024).toFixed(1)} KB
                      </span>
                    </div>
                  </div>
                  <button
                    type="button"
                    data-testid="ocr-autofill"
                    disabled={ocrLoading}
                    onClick={handleOcr}
                    className="inline-flex items-center justify-center gap-2 w-full px-4 py-2.5 text-sm font-semibold text-white bg-[#0070F2] hover:bg-[#005BCA] disabled:bg-slate-300 disabled:cursor-not-allowed rounded-md transition-colors shadow-sm"
                  >
                    <Sparkles className="w-4 h-4" />
                    {ocrLoading ? "Reading receipt…" : "AI-fill fields from receipt"}
                  </button>
                  {ocrError && (
                    <p
                      className="text-xs text-red-600"
                      data-testid="ocr-error"
                    >
                      {ocrError}
                    </p>
                  )}
                </>
              )}
            </div>
          )}

          <div className="flex flex-col gap-1.5">
            <div className="flex items-center">
              <label
                htmlFor="new-expense-vendor"
                className="text-xs font-medium text-slate-700"
              >
                Vendor <span className="text-red-500">*</span>
              </label>
              {ocrChips.vendor && (
                <span
                  data-testid="ocr-chip-vendor"
                  className="ml-2 inline-flex items-center gap-1 text-[10px] font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5"
                >
                  Suggested by OCR
                  <button
                    type="button"
                    onClick={() =>
                      setOcrChips((s) => ({ ...s, vendor: false }))
                    }
                    aria-label="Dismiss"
                  >
                    ×
                  </button>
                </span>
              )}
            </div>
            <input
              id="new-expense-vendor"
              data-testid="new-expense-vendor"
              type="text"
              value={vendor}
              onChange={(e) => {
                setVendor(e.target.value);
                if (ocrChips.vendor)
                  setOcrChips((s) => ({ ...s, vendor: false }));
                if (errors.vendor)
                  setErrors((s) => {
                    const { vendor: _drop, ...rest } = s;
                    return rest;
                  });
              }}
              placeholder="e.g. Stripe"
              className="px-3 py-2 text-sm rounded-md border border-slate-200 bg-white focus:outline-none focus:ring-2 focus:ring-[#0070F2]/30 focus:border-[#0070F2]"
            />
            {errors.vendor && (
              <p
                data-testid="error-vendor"
                className="mt-1 text-xs text-red-600"
              >
                {errors.vendor}
              </p>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="flex flex-col gap-1.5">
              <div className="flex items-center">
                <label
                  htmlFor="new-expense-date"
                  className="text-xs font-medium text-slate-700"
                >
                  Date <span className="text-red-500">*</span>
                </label>
                {ocrChips.occurredOn && (
                  <span
                    data-testid="ocr-chip-date"
                    className="ml-2 inline-flex items-center gap-1 text-[10px] font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5"
                  >
                    Suggested by OCR
                    <button
                      type="button"
                      onClick={() =>
                        setOcrChips((s) => ({ ...s, occurredOn: false }))
                      }
                      aria-label="Dismiss"
                    >
                      ×
                    </button>
                  </span>
                )}
              </div>
              <input
                id="new-expense-date"
                data-testid="new-expense-date"
                type="date"
                value={occurredOn}
                onChange={(e) => {
                  setOccurredOn(e.target.value);
                  if (ocrChips.occurredOn)
                    setOcrChips((s) => ({ ...s, occurredOn: false }));
                  if (errors.occurredOn)
                    setErrors((s) => {
                      const { occurredOn: _drop, ...rest } = s;
                      return rest;
                    });
                }}
                className="px-3 py-2 text-sm rounded-md border border-slate-200 bg-white focus:outline-none focus:ring-2 focus:ring-[#0070F2]/30 focus:border-[#0070F2]"
              />
              {errors.occurredOn && (
                <p
                  data-testid="error-occurredOn"
                  className="mt-1 text-xs text-red-600"
                >
                  {errors.occurredOn}
                </p>
              )}
            </div>
            <div className="flex flex-col gap-1.5">
              <div className="flex items-center">
                <label
                  htmlFor="new-expense-amount"
                  className="text-xs font-medium text-slate-700"
                >
                  Amount (USD) <span className="text-red-500">*</span>
                </label>
                {ocrChips.amount && (
                  <span
                    data-testid="ocr-chip-amount"
                    className="ml-2 inline-flex items-center gap-1 text-[10px] font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5"
                  >
                    Suggested by OCR
                    <button
                      type="button"
                      onClick={() =>
                        setOcrChips((s) => ({ ...s, amount: false }))
                      }
                      aria-label="Dismiss"
                    >
                      ×
                    </button>
                  </span>
                )}
              </div>
              <input
                id="new-expense-amount"
                data-testid="new-expense-amount"
                type="text"
                inputMode="decimal"
                value={amountText}
                onChange={(e) => {
                  setAmountText(e.target.value);
                  if (ocrChips.amount)
                    setOcrChips((s) => ({ ...s, amount: false }));
                  if (errors.amount)
                    setErrors((s) => {
                      const { amount: _drop, ...rest } = s;
                      return rest;
                    });
                }}
                placeholder="129.00"
                className="px-3 py-2 text-sm rounded-md border border-slate-200 bg-white focus:outline-none focus:ring-2 focus:ring-[#0070F2]/30 focus:border-[#0070F2]"
              />
              {errors.amount && (
                <p
                  data-testid="error-amount"
                  className="mt-1 text-xs text-red-600"
                >
                  {errors.amount}
                </p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="flex flex-col gap-1.5">
              <div className="flex items-center flex-wrap gap-x-2">
                <label
                  htmlFor="new-expense-category"
                  className="text-xs font-medium text-slate-700"
                >
                  Category <span className="text-red-500">*</span>
                </label>
                {vendorDefaultChips?.category && (
                  <span
                    data-testid="vendor-default-category"
                    className="inline-flex items-center gap-1 text-[10px] font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-1.5 py-0.5"
                  >
                    From {vendorDefaultChips.vendorLabel} history
                    <button
                      type="button"
                      onClick={() =>
                        setVendorDefaultChips((s) =>
                          s ? { ...s, category: false } : s,
                        )
                      }
                      aria-label="Dismiss"
                    >
                      ×
                    </button>
                  </span>
                )}
              </div>
              {/*
                Using a native <select> here rather than the Radix Select:
                the Radix version portals its content outside the dialog,
                which complicates Playwright selectors keyed by data-testid.
                Native selects keep the form trivially testable and
                accessible with zero extra markup.
              */}
              <select
                id="new-expense-category"
                data-testid="new-expense-category"
                value={categoryId}
                onChange={(e) => {
                  setCategoryId(e.target.value);
                  if (errors.categoryId)
                    setErrors((s) => {
                      const { categoryId: _drop, ...rest } = s;
                      return rest;
                    });
                }}
                className="px-3 py-2 text-sm rounded-md border border-slate-200 bg-white focus:outline-none focus:ring-2 focus:ring-[#0070F2]/30 focus:border-[#0070F2]"
              >
                <option value="">Select a category…</option>
                {categories.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.label}
                  </option>
                ))}
              </select>
              {errors.categoryId && (
                <p
                  data-testid="error-categoryId"
                  className="mt-1 text-xs text-red-600"
                >
                  {errors.categoryId}
                </p>
              )}
              {categories.find((c) => c.id === categoryId)?.code === "entertainment" && (
                <p
                  data-testid="expense-category-entertainment-warning"
                  className="mt-1 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1"
                >
                  Client entertainment is <strong>0% deductible</strong> since
                  TCJA 2017 (IRC § 274(a)). Meals at entertainment venues are
                  still 50% if separately itemized.
                </p>
              )}
            </div>
            <div className="flex flex-col gap-1.5">
              <div className="flex items-center flex-wrap gap-x-2">
                <label
                  htmlFor="new-expense-tax-policy"
                  className="text-xs font-medium text-slate-700"
                >
                  Tax policy
                </label>
                {vendorDefaultChips?.taxPolicy && (
                  <span
                    data-testid="vendor-default-taxpolicy"
                    className="inline-flex items-center gap-1 text-[10px] font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-1.5 py-0.5"
                  >
                    From {vendorDefaultChips.vendorLabel} history
                    <button
                      type="button"
                      onClick={() =>
                        setVendorDefaultChips((s) =>
                          s ? { ...s, taxPolicy: false } : s,
                        )
                      }
                      aria-label="Dismiss"
                    >
                      ×
                    </button>
                  </span>
                )}
              </div>
              {/*
                When the selected category has matching policies we present
                them under a "Suggested for {label}" optgroup and hide the
                rest behind a "Show all policies" toggle. "None" is always
                available at the top, and the chosen policy id stays valid
                regardless of what is currently visible (hiding an option
                from the DOM does NOT clear the underlying state).
              */}
              <select
                id="new-expense-tax-policy"
                data-testid="new-expense-tax-policy"
                value={taxPolicyId}
                onChange={(e) => setTaxPolicyId(e.target.value)}
                className="px-3 py-2 text-sm rounded-md border border-slate-200 bg-white focus:outline-none focus:ring-2 focus:ring-[#0070F2]/30 focus:border-[#0070F2]"
              >
                <option value="">None</option>
                {hasSuggestions && selectedCategoryLabel ? (
                  <>
                    <optgroup
                      data-testid="new-expense-tax-policy-suggested-heading"
                      label={`Suggested for ${selectedCategoryLabel}`}
                    >
                      {suggestedPolicies.map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.category} ({p.region})
                        </option>
                      ))}
                    </optgroup>
                    {showAllPolicies && (
                      <optgroup label="Other policies">
                        {otherPolicies.map((p) => (
                          <option key={p.id} value={p.id}>
                            {p.category} ({p.region})
                          </option>
                        ))}
                      </optgroup>
                    )}
                  </>
                ) : (
                  otherPolicies.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.category} ({p.region})
                    </option>
                  ))
                )}
              </select>
              {hasSuggestions && !showAllPolicies && (
                <button
                  type="button"
                  data-testid="new-expense-tax-policy-show-all"
                  onClick={() => setShowAllPolicies(true)}
                  className="self-start text-xs font-medium text-[#0070F2] hover:text-[#005BCA]"
                >
                  Show all policies
                </button>
              )}
              {hasSuggestions && showAllPolicies && (
                <button
                  type="button"
                  data-testid="new-expense-tax-policy-show-suggested"
                  onClick={() => setShowAllPolicies(false)}
                  className="self-start text-xs font-medium text-slate-500 hover:text-slate-700"
                >
                  Show suggested only
                </button>
              )}
            </div>
          </div>

          <div className="flex flex-col gap-1.5">
            <label
              htmlFor="new-expense-purpose"
              className="text-xs font-medium text-slate-700"
            >
              Business purpose
            </label>
            <textarea
              id="new-expense-purpose"
              data-testid="new-expense-purpose"
              rows={2}
              value={businessPurpose}
              onChange={(e) => setBusinessPurpose(e.target.value)}
              placeholder="Optional — e.g. Q1 renewal dinner with TechCorp"
              className="px-3 py-2 text-sm rounded-md border border-slate-200 bg-white focus:outline-none focus:ring-2 focus:ring-[#0070F2]/30 focus:border-[#0070F2] resize-none"
            />
          </div>

          {error && (
            <div
              data-testid="new-expense-error"
              className="text-xs font-medium text-red-600"
            >
              {error}
            </div>
          )}
        </div>

        <DialogFooter>
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-200 hover:bg-slate-50 rounded-md transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            data-testid="new-expense-submit"
            onClick={handleSubmit}
            disabled={submitting}
            className="px-4 py-2 text-sm font-medium text-white bg-[#0070F2] hover:bg-[#005BCA] disabled:bg-slate-300 disabled:cursor-not-allowed rounded-md transition-colors"
          >
            {submitting ? "Saving…" : submitLabel}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
